import { useEffect, useState } from 'react';
import {
    Text,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Box,
    Flex,
    IconButton,
    Input,
    InputGroup,
    InputRightElement,
    useDisclosure,
} from '@chakra-ui/react';
import { ChevronDownIcon, CloseIcon } from '@chakra-ui/icons';

import AddItemButton from '../ui/AddItemButton';
import { CheckMarkIcon, EmptyStateInfinite } from '@assets/icons';
import { useGetInfiniteListMasterProduct } from '@pages/UploadMappingV2/hooks/query/useGetListMasterProduct';
import InfiniteScrollObserver from '../InfiniteScrollObserver';
import { IterationComponent } from '@components/functionComponent';
import SkeletonComponent from '@components/skeleton';
import { usePieceStore } from '../../hooks/state/useAddProductSatuan';
import { useDebounce } from '@hooks/useDebounce';
import EmptyState from '../SearchProduct/EmptyState';

export default function TableAddProductSatuan({ tableHeadLists = [], tableData = [] }) {
    if (!tableHeadLists || !tableData) return <Text>There was an error displaying your data!</Text>;

    const {
        piece,
        pieces,
        errors,
        addPiece,
        setCode,
        updatePiece,
        isEditing,
        isEditingRow,
        setIsEditingRow,
        resetError,
    } = usePieceStore();

    const [selected, setSelected] = useState(null);
    const [value, setValue] = useState(piece.name ?? null);
    const { isOpen, onOpen, onClose } = useDisclosure({ defaultIsOpen: true });
    const debouncedValue = useDebounce(value, 500);

    const {
        data: infiniteData,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
        isLoading: isLoadingInfiniteData,
    } = useGetInfiniteListMasterProduct({ search: debouncedValue });
    const showEmpty = !isLoadingInfiniteData && value?.length > 3 && !infiniteData?.length;

    const handleSelect = (product) => () => {
        const { code, tempCode, ...objectWithoutCode } = product;
        const localId = isEditing ? piece.localId : pieces.length + 1;
        setSelected({ ...objectWithoutCode, localId });
        setValue(product.name);
    };

    const handleAddProduct = () => {
        if (!selected) return;

        if (isEditing) {
            updatePiece(piece.localId, { ...selected, code: piece.code });
            setIsEditingRow(null);
            onClose();
        } else {
            addPiece(selected);
            onClose();
        }
    };

    const handleCloseItems = () => {
        setIsEditingRow(false);
        onClose();
        setSelected(null);
        resetError();
    };

    const handleSearch = (e) => {
        setValue(e.target.value);
        setSelected(null);
    };

    const tableHeadMapping = () => {
        return tableHeadLists.map((value) => (
            <Th
                textTransform={'none'}
                fontWeight={'bold'}
                color={'semantic.600'}
                fontFamily={'inherit'}
                fontSize={'14px'}
                p={'12px'}
                letterSpacing={'normal'}
                width={value.width ?? '200px'}
                textAlign={value.align ?? 'left'}
            >
                {value.title}
            </Th>
        ));
    };

    const tableBodyMapping = () => {
        const shouldShowInput = isOpen || errors.name || isEditingRow === piece.localId;
        if (shouldShowInput) {
            return inputProduct();
        }
        if (!piece.name.trim()) {
            return null;
        }
        return (
            <Tr>
                {tableHeadLists.map((list) => (
                    <Td p={'12px'} fontSize={'14px'} textAlign={list.align ?? 'left'}>
                        {list.tableDataList({ value: piece[list.key], allValue: piece })}
                    </Td>
                ))}
            </Tr>
        );
    };

    const productLists = () => {
        const renderItem = (value) => (
            <Box
                key={value.localId}
                p="12px"
                transition=".3s all ease"
                cursor="pointer"
                borderRadius="8px"
                _hover={{ bg: 'semantic.200' }}
                onClick={handleSelect(value)}
            >
                <Text mb="4px">{value.name}</Text>
                <Text color="neutral.500" fontSize="14px">
                    {value.barcode}
                </Text>
            </Box>
        );

        const renderSkeleton = () => (
            <IterationComponent
                arr={[1, 2, 3]}
                render={() => (
                    <Box bg="semantic.200" mb="8px" p="12px" borderRadius="8px">
                        <SkeletonComponent width="50%" height="20px" mb="4px" />
                        <SkeletonComponent width="25%" height="17px" />
                    </Box>
                )}
            />
        );

        const Section = ({ title, children }) => (
            <Box mb="12px">
                <Text color="neutral.600" textTransform="uppercase" mb="8px" fontSize="14px">
                    {title}
                </Text>
                {children}
            </Box>
        );

        return (
            <Box
                p="22px"
                borderRadius="8px"
                bg="white"
                shadow="sm"
                position="absolute"
                h="fit-content"
                maxH="400px"
                overflowY={showEmpty ? 'hidden' : 'scroll'}
                w="full"
                top="70px"
                zIndex={999}
            >
                {pieces.length > 0 && <Section title="Produk Sebelumnya">{pieces?.map(renderItem)}</Section>}

                {isLoadingInfiniteData && renderSkeleton()}

                {showEmpty && <EmptyState />}

                {!showEmpty && infiniteData?.length > 0 && (
                    <Section title="Produk Lainnya">
                        {infiniteData?.map(renderItem)}

                        {hasNextPage && (
                            <>
                                <InfiniteScrollObserver
                                    hasNextPage={hasNextPage}
                                    fetchNextPage={fetchNextPage}
                                    disabled={isFetchingNextPage}
                                    rootMargin="900px"
                                />
                                {isFetchingNextPage && renderSkeleton()}
                            </>
                        )}
                    </Section>
                )}
            </Box>
        );
    };

    const inputProduct = () => {
        const colSpan = !isEditing ? tableHeadLists.length - 1 : tableHeadLists.length - 2;

        return (
            <Tr>
                <Td colSpan={colSpan} p={'12px'} verticalAlign={'baseline'}>
                    <InputGroup position={'relative'}>
                        <Input
                            placeholder={'Cari nama atau kode produk Paragon...'}
                            value={value}
                            onChange={handleSearch}
                            isInvalid={errors?.name}
                        />
                        <InputRightElement mx={'12px'} onClick={() => {}}>
                            <ChevronDownIcon fontSize={'24px'} />
                        </InputRightElement>
                        {value?.length > 3 && !selected && productLists()}
                    </InputGroup>

                    <Text fontSize={'10px'} mt={'4px'} color={'danger.500'}>
                        {errors.name}
                    </Text>
                </Td>

                {isEditing && (
                    <Td p={'12px'} verticalAlign={'baseline'}>
                        <Input
                            placeholder={'Tuliskan kode produk toko Anda'}
                            value={piece.code}
                            onChange={(e) => {
                                setCode(e.target.value);
                            }}
                            isInvalid={errors?.code}
                        />

                        <Text fontSize={'10px'} mt={'4px'} color={'danger.500'}>
                            {errors.code}
                        </Text>
                    </Td>
                )}

                <Td p={'12px'}>
                    <Flex gap={'12px'}>
                        <IconButton maxW={'30px'} size={'sm'} onClick={handleAddProduct} disabled={!selected}>
                            <Box>
                                <CheckMarkIcon color="#fff" size={18} />
                            </Box>
                        </IconButton>
                        <IconButton maxW={'30px'} size={'sm'} variant={'outline'} onClick={handleCloseItems}>
                            <CloseIcon fontSize={'10px'} />
                        </IconButton>
                    </Flex>
                </Td>
            </Tr>
        );
    };

    useEffect(() => {
        if (isEditing) {
            setValue(piece.name);
            setSelected(piece);
        }
    }, [isEditing]);

    return (
        <Table variant="simple" minW={'800px'}>
            <Thead>
                <Tr>{tableHeadMapping()}</Tr>
            </Thead>

            <Tbody>
                {tableBodyMapping()}

                {!isOpen && !piece.name.trim() && !errors.name && (
                    <Tr>
                        <Td colSpan={tableHeadLists.length} p={'12px'}>
                            <AddItemButton btnTitle="Tambah Produk" onClick={onOpen} />
                        </Td>
                    </Tr>
                )}
            </Tbody>
        </Table>
    );
}
